import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import {
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
  FileInput,
} from "@/components/ui/file-uploader";
import { DropzoneOptions } from "react-dropzone";
import { useFormContext, Controller } from "react-hook-form";
import { Facility } from "@/types/facility";
import { FacilityAddOnType, FacilityStatus, FacilityType } from "@/types/enums";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useFieldArray } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useGetCategoriesQuery } from "@/services/categoryApi";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { deleteCognitoImage, UploadCognitoImage } from "@/lib/utils/s3Service";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import Combobox from "@/components/ui/common/combobox";
import { Spinner } from "@/components/ui/spinner/spinner";
import { useToast } from "@/components/ui/use-toast";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetAllProductsQuery } from "@/services/productApi";

const { VITE_VIEW_S3_URL } = import.meta.env;

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const GeneralInfoForm = ({
  action,
  open,
}: FormStepProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { toast } = useToast();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [defaultCategoryImageUrl, setDefaultCategoryImageUrl] = useState("");

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    register,
    getValues,
  } = useFormContext<Facility>();
  const watcher = watch();

  // Lightbox state for viewing add-on facility images as a gallery
  const [gallery, setGallery] = useState<{ open: boolean; images: string[]; index: number }>(
    { open: false, images: [], index: 0 }
  );

  const { data: categoryData, isLoading } = useGetCategoriesQuery({
    org_id: orgId,
    query: "type=facility&load_subcategories=true&only_parent=true&sort_key=id&sort_order=desc",
  });


  const { data: coachData } = useGetCoachesQuery({
    query: "is_trainer=true&sort_key=id&sort_order=desc",
    org_id: orgId,
  });

  const { data: productData } = useGetAllProductsQuery({
    query: "status=active&sort_key=id&sort_order=desc",
    org_id: orgId,
  });

  const coachOptions = useMemo(() => {
    if (!Array.isArray(coachData?.data)) {
      return [];
    }
    return coachData.data.map((coach) => {
      const firstName = (coach?.first_name || "").trim();
      const lastName = (coach?.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      const labelPrefix = coach.own_coach_id ? `${coach.own_coach_id} • ` : "";
      const label = `${labelPrefix}${fullName}`;

      return {
        label: label,
        value: coach.id?.toString() || "",
      };
    });
  }, [coachData]);


  const typeOptions = [
    { label: "Default", value: FacilityType.Default },
    { label: "Trainer", value: FacilityType.Trainer },
    { label: "Consultation", value: FacilityType.Consultation },
  ];

  const addOnitemTypeOptions = [
    { label: "Facility", value: "facility" },
    { label: "Product", value: "product" },
    { label: "Consultation", value: "consultation" },
    { label: "Trainer", value: "trainer" },
  ];

  const { data: facilityAsAddOns } = useGetFacilitiesQuery({
    query: "is_add_on=true&status=active",
    orgId: orgId,
  });

  const { data: allFacilitiesData } = useGetFacilitiesQuery({
    query: "status=active&sort_key=name&sort_order=asc",
    orgId: orgId,
  });

  const parentFacilityOptions = useMemo(() => {
    if (!Array.isArray(allFacilitiesData?.data)) return [];
    const currentId = getValues("id");
    return allFacilitiesData.data
      .filter((fac) => (!currentId || fac.id !== currentId) && !fac.parent_id)
      .map((fac) => ({
        label: fac.name,
        value: fac.id?.toString() || "",
      }));
  }, [allFacilitiesData, getValues]);

  const categoryOptions = useMemo(() => {
    return Array.isArray(categoryData?.data)
      ? categoryData?.data.map((category) => ({
        label: category?.name || "",
        value: category?.id?.toString() || "",
      }))
      : [];
  }, [categoryData]);

  const selectedCategory = useMemo(() => {
    if (!watcher.category_id || !Array.isArray(categoryData?.data)) return undefined;
    return categoryData.data.find(
      (category) => category.id === Number(watcher.category_id)
    );
  }, [categoryData, watcher.category_id]);

  const selectedCategoryImage = selectedCategory?.image_url || "";

  const categoryImageUrls = useMemo(() => {
    return Array.isArray(categoryData?.data)
      ? categoryData.data
          .map((category) => category.image_url)
          .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
      : [];
  }, [categoryData]);

  const resolveImageUrl = (imageUrl?: string | null) => {
    if (!imageUrl) return "";
    return imageUrl.includes("https://") ? imageUrl : `${VITE_VIEW_S3_URL}/${imageUrl}`;
  };

  const subcategoryOptions = useMemo(() => {
    if (!watcher.category_id || !Array.isArray(categoryData?.data)) return [];
    return Array.isArray(selectedCategory?.subcategories)
      ? selectedCategory.subcategories.map((sub) => ({
        label: sub.name || "",
        value: sub.id?.toString() || "",
      }))
      : [];
  }, [selectedCategory, watcher.category_id]);

  useEffect(() => {
    if (!open || !selectedCategoryImage) return;

    const currentImages = getValues("image_url") || [];
    const hasOnlyPreviousCategoryDefault =
      currentImages.length === 1 && currentImages[0] === defaultCategoryImageUrl;

    if (currentImages.length === 0 || hasOnlyPreviousCategoryDefault) {
      setValue("image_url", [selectedCategoryImage], {
        shouldDirty: action === "edit",
        shouldValidate: true,
      });
      setDefaultCategoryImageUrl(selectedCategoryImage);
    }
  }, [action, defaultCategoryImageUrl, getValues, open, selectedCategoryImage, setValue]);

  const addOnOptions = useMemo(() => {
    return Array.isArray(facilityAsAddOns?.data)
      ? facilityAsAddOns?.data.filter((facility) => 
        facility.facility_type == "default").map((facility) => ({
        ...facility,
        label: facility?.name || "",
        value: facility?.id?.toString() || "",
      }))
      : [];
  }, [facilityAsAddOns]);

  const productOptions = useMemo(() => {
    return Array.isArray(productData?.data)
      ? productData?.data
          .filter((product) => {
            if (product.manage_inventory && (product.quantity === undefined || product.quantity <= 0)) {
              return false;
            }
            return true;
          })
          .map((product) => ({
            ...product,
            label: product?.product_name || "",
            value: product?.id?.toString() || "",
          }))
      : [];
  }, [productData]);

  const trainerOptions = useMemo(() => {
    return Array.isArray(facilityAsAddOns?.data)
      ? facilityAsAddOns?.data.filter((facility) => facility.facility_type === "trainer").map((facility) => ({
        ...facility,
        label: facility?.name || "",
        value: facility?.id?.toString() || "",
      }))
      : [];
  }, [facilityAsAddOns]);

  const consultationOptions = useMemo(() => {
    return Array.isArray(facilityAsAddOns?.data)
      ? facilityAsAddOns?.data.filter((facility) => facility.facility_type === "consultation").map((facility) => ({
        ...facility,
        label: facility?.name || "",
        value: facility?.id?.toString() || "",
      }))
      : [];
  }, [facilityAsAddOns]);


  // Helper function to check for duplicate add-ons
      const checkForDuplicateAddOn = (
        id: number,
        type: "facility" | "product" | "consultation" | "trainer",
        currentIndex: number
      ) => {
        const currentAddOns = getValues("add_ons") || [];

        return currentAddOns.some(
          (addOn, addOnIndex) =>
            addOnIndex !== currentIndex &&
            addOn.item_type === type &&
            addOn.item_id === id
        );
      };

  // Inside your form component, add this near other useFieldArray declarations
  const {
    fields: addOnFields,
    append: appendAddOn,
    remove: removeAddOn,
  } = useFieldArray({
    control,
    name: "add_ons",
  });

  const isBookable = watch("bookable");
  const isAddOn = watch("is_add_on");

  const handleFileUpload = async (files: File[] | null) => {
    if (files && files.length > 0) {
      setIsUploadingImages(true);
      try {
        const currentImages = getValues("image_url") || [];
        const uploadPromises = files.map((file) => UploadCognitoImage(file));
        const uploadResults = await Promise.all(uploadPromises);
        const newImageUrls = uploadResults.map((result) => result.location);
        const allImages = [...currentImages, ...newImageUrls];
        setValue("image_url", allImages as string[]);
      } catch (error) {
        console.error("Error uploading images:", error);
      } finally {
        setIsUploadingImages(false);
      }
    }
  };

  const handleRemoveImage = async (urlToRemove: string, index: number) => {
    try {
      if (!categoryImageUrls.includes(urlToRemove)) {
        await deleteCognitoImage(urlToRemove);
      }
    } catch (e) {
      console.error("Error deleting from S3:", e);
    } finally {
      const currentImages: string[] = getValues("image_url") || [];
      const updated = currentImages.filter((_, i) => i !== index);
      setValue("image_url", updated);
    }
  };

  // No per add-on upload. Images are shown from selected add-on facility

  const dropzoneConfig = {
    accept: {
      "image/": [".jpg", ".jpeg", ".png"],
    },
    multiple: true,
    maxFiles: 5, // max 5 images
    maxSize: 1 * 1024 * 1024, // 1MB
  } satisfies DropzoneOptions;

  console.log({ watcher, errors });
  return (
    <div className="space-y-4">
      {/* section 1 */}
      <Card className="p-4">
        <p className="font-medium text-base">General Information</p>
        <Separator className="h-[1px]" />
        <CardContent className="py-4 px-0 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label>
                Facility Name <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Required",
                  maxLength: {
                    value: 50,
                    message: "Name should not exceed 50 characters",
                  },
                  validate: (value) => {
                    if (!value || value.trim().length === 0) {
                      return "Name Should not be empty";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input placeholder="Enter facility name" {...field} />
                )}
              />
              {errors.name && (
                <span className="text-red-500 text-xs">
                  {errors.name.message}
                </span>
              )}
            </div>

            <div className="space-y-1">
              <Label>
                Min Req Credits <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="min_limit"
                control={control}
                rules={{
                  required: "Required",
                  min: { value: 0, message: "Min credits must be positive" },
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === '.') {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter min credits"
                    {...field}
                  />
                )}
              />
              {errors.min_limit && (
                <span className="text-red-500 text-xs">
                  {errors.min_limit.message}
                </span>
              )}
            </div>

            <div className="space-y-1">
              <Label>
                Max Capacity <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="max_capacity"
                control={control}
                rules={{
                  required: "Required",
                  min: { value: 1, message: "Max capacity must be at least 1" },
                  validate: (value) =>
                    Number.isInteger(Number(value)) || "Decimal not allowed",
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    placeholder="Enter max capacity"
                    onKeyDown={(e) => {
                      if (e.key === '.') {
                        e.preventDefault();
                      }
                    }}
                    {...field}
                  />
                )}
              />
              {errors.max_capacity && (
                <span className="text-red-500 text-xs">
                  {errors.max_capacity.message}
                </span>
              )}
            </div>


            <div className="space-y-1">
              <Label>
                Category <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="category_id"
                control={control}
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <Combobox
                    list={categoryOptions}
                    defaultValue={categoryOptions.find(option => option.value === value?.toString())?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = categoryOptions.find(option => option.label === selectedLabel);
                      onChange(selectedOption ? Number(selectedOption.value) : undefined);
                      setValue("subcategory_id", undefined);
                    }}
                    label="Category"
                    showLabel={false}
                  />
                )}
              />
              {errors.category_id && (
                <span className="text-red-500 text-xs">
                  {errors.category_id.message}
                </span>
              )}
            </div>

            <div className="space-y-1">
              <Label>Sub Category</Label>
              <Controller
                name="subcategory_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Combobox
                    list={subcategoryOptions}
                    defaultValue={subcategoryOptions.find(option => option.value === value?.toString())?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = subcategoryOptions.find(option => option.label === selectedLabel);
                      onChange(selectedOption ? Number(selectedOption.value) : undefined);
                    }}
                    label="Subcategory"
                    showLabel={false}
                  />
                )}
              />
            </div>

            <div className="space-y-1">
              <Label>
                Parent Facility <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
              </Label>
              <Controller
                name="parent_id"
                control={control}
                render={({ field: { value, onChange } }) => {
                  const optionsWithNone = [
                    { label: "None", value: "" },
                    ...parentFacilityOptions,
                  ];
                  const currentSelected = parentFacilityOptions.find(
                    (option) => option.value === (value ? value.toString() : "")
                  );
                  return (
                    <Combobox
                      list={optionsWithNone}
                      defaultValue={currentSelected?.label || ""}
                      setFilter={(selectedLabel: string) => {
                        const selectedOption = optionsWithNone.find(
                          (option) => option.label === selectedLabel
                        );
                        onChange(
                          selectedOption && selectedOption.value
                            ? Number(selectedOption.value)
                            : null
                        );
                      }}
                      label="Parent Facility"
                      showLabel={false}
                    />
                  );
                }}
              />
            </div>

            <div className="space-y-1">
              <Label>
                Status <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="status"
                control={control}
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onChange(FacilityStatus.Active)}
                      className={cn(
                        "py-1 px-3 h-10 text-sm w-full",
                        value == FacilityStatus.Active
                          ? "bg-primary "
                          : "bg-gray-100 text-gray-700"
                      )}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(FacilityStatus.Inactive)}
                      className={cn(
                        "py-1 px-3 h-10 text-sm w-full",
                        value == FacilityStatus.Inactive
                          ? "bg-primary "
                          : "bg-gray-100 text-gray-700"
                      )}
                    >
                      Inactive
                    </button>
                  </div>
                )}
              />
            </div>

            <div className="space-y-1 flex items-center jus gap-2">
              <Controller
                name="access_control"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label className="text-sm">Access Control</Label>
            </div>

            <div className="space-y-1 flex items-center gap-2">
              <Controller
                name="credit_only"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label>Credit Only</Label>
            </div>

            <div className="row-span-2">
              <Label>Facility Image</Label>
              <FileUploader
                value={null}
                onValueChange={handleFileUpload}
                dropzoneOptions={{
                  ...dropzoneConfig,
                  disabled: isUploadingImages,
                }}
              >
                <FileInput>
                  <div className={cn(
                    "flex items-center justify-center h-12 w-full border bg-background rounded-md",
                    isUploadingImages && "opacity-50 cursor-not-allowed"
                  )}>
                    {isUploadingImages ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="small" />
                        <p className="text-gray-400">Uploading images...</p>
                      </div>
                    ) : (
                      <p className="text-gray-400">Drop images here (max 5)</p>
                    )}
                  </div>
                </FileInput>

                <FileUploaderContent className="grid grid-cols-3 items-center gap-2 min-h-20 max-h-fit">
                  {watcher.image_url?.map((url, index) => (
                    <FileUploaderItem
                      key={index}
                      index={index}
                      onRemove={async () => {
                        await handleRemoveImage(url, index);
                      }}
                      className="h-16 p-0 rounded-md overflow relative"
                      aria-roledescription={`uploaded image ${index + 1}`}
                    >
                      <img
                        src={resolveImageUrl(url)}
                        alt={`Uploaded image ${index + 1}`}
                        className="size-12 object-cover"
                      />
                    </FileUploaderItem>
                  ))}
                </FileUploaderContent>
              </FileUploader>
            </div>

            {watcher.facility_type!=="trainer" && (<div className="space-y-1 flex items-center gap-2">
              <Controller
                name="is_add_on"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                      if (value == true) {
                        setValue("add_ons", []);
                        setValue("bookable", false)
                      } else {

                        appendAddOn({
                          item_type: "facility",
                          item_id: undefined,
                          type: FacilityAddOnType.Inclusive,
                          description: "",
                        });
                      }
                    }}
                  />
                )}
              />
              <Label>Set as Add On</Label>
            </div>)}

            {!isAddOn && <div className="space-y-1 flex items-center gap-2">
              <Controller
                name="bookable"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label>Bookable</Label>
            </div>}

            {isBookable && !watcher.is_trainer_facility && (
              <>
                <div className="space-y-1 flex items-center gap-2">
                  <Controller
                    name="shareable_slots"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label>Shareable slots</Label>
                </div>

                <div className="space-y-1 flex items-center gap-2">
                  <Controller
                    name="require_approval"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label>Require Approval</Label>
                </div>

              </>
            )}

            <div className="space-y-1">
              <Label>Facility Type</Label>
              <Controller
                name="facility_type"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Combobox
                    list={typeOptions}
                    defaultValue={typeOptions.find(option => option.value === value)?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = typeOptions.find(option => option.label === selectedLabel);
                      const selectedValue = selectedOption?.value as FacilityType;
                      onChange(selectedValue);

                      if (selectedValue === FacilityType.Trainer) {
                        setValue("is_trainer_facility", true);
                        setValue("require_approval", false);
                      } else {
                        setValue("is_trainer_facility", false);
                        setValue("coach_id", undefined);
                      }
                    }}
                    label="Facility Type"
                    showLabel={false}
                    disabled={action == "edit"}
                  />
                )}
              />
            </div>

            {(watcher.facility_type === FacilityType.Trainer || watcher.is_trainer_facility) && (
              <div className="space-y-1">
                <Label>Trainer</Label>
                <Controller
                  name="coach_id"
                  rules={{ required: (watcher.facility_type === FacilityType.Trainer || watcher.is_trainer_facility) ? "Required" : false }}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Combobox
                      list={coachOptions}
                      defaultValue={coachOptions.find(option => option.value === value?.toString())?.label || ""}
                      setFilter={(selectedLabel: string) => {
                        const selectedOption = coachOptions.find(option => option.label === selectedLabel);
                        onChange(selectedOption ? Number(selectedOption.value) : undefined);
                      }}
                      label="Trainer"
                      disabled={action == "edit"}
                      showLabel={false}
                    />
                  )}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* section 2 */}
      {!isAddOn && watcher.facility_type !== FacilityType.Trainer && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <p className="font-medium text-base">Add Ons</p>
            {addOnFields.length == 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                  appendAddOn({
                    item_type: "facility",
                    item_id: undefined,
                    type: FacilityAddOnType.Inclusive,
                    description: "",
                  })
                }
              >
                <Plus className="text-primary" />
              </Button>
            )}
          </div>
          <Separator className="h-[1px]" />
          {addOnFields.map((field, index) => (
            <CardContent
              key={field.id}
              className="py-4 px-0 space-y-4 flex items-center gap-2 border-b border-gray-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-3 w-full">
                <div className="space-y-1">
                  <Label>
                    Type <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name={`add_ons.${index}.item_type`}
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field: { value, onChange } }) => (
                      <Combobox
                        list={addOnitemTypeOptions}
                        defaultValue={addOnitemTypeOptions.find(option => option.value === value)?.label || ""}
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = addOnitemTypeOptions.find(option => option.label === selectedLabel);

                          if (selectedOption) {
                            onChange(selectedOption.value);
                            // Reset associated fields when type changes
                            setValue(`add_ons.${index}.item_id`, undefined);
                            setValue(`add_ons.${index}.access_control`, undefined);
                          }
                        }}
                        label="Type"
                        showLabel={false}
                      />
                    )}
                  />
                </div>

                <div className="space-y-1">
                  <Label>
                    Item <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name={`add_ons.${index}.item_id`}
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field: { value, onChange } }) => {
                      const currentType = watch(`add_ons.${index}.item_type`);

                      let currentOptions: { label: string; value: string }[] = [];
                      switch (currentType) {
                        case "facility":
                          currentOptions = addOnOptions;
                          break;
                        case "product":
                          currentOptions = productOptions;
                          break;
                        case "trainer":
                          currentOptions = trainerOptions;
                          break;
                        case "consultation":
                          currentOptions = consultationOptions;
                          break;
                        default:
                          currentOptions = [];
                      }

                      return (
                        <Combobox
                          list={currentOptions}
                          defaultValue={currentOptions.find(option => option.value === value?.toString())?.label || ""}
                          setFilter={(selectedLabel: string) => {
                            const selectedOption = currentOptions.find(option => option.label === selectedLabel);
                            const itemId = selectedOption ? Number(selectedOption.value) : undefined;

                            if (currentType === "product" && itemId) {
                              const product = productData?.data?.find(p => p.id === itemId);
                              if (product && product.manage_inventory && (product.quantity === undefined || product.quantity <= 0)) {
                                toast({
                                  variant: "destructive",
                                  title: "Out of Stock",
                                  description: "This product has no available stock and cannot be added as an add-on.",
                                });
                                return;
                              }
                            }

                            if (itemId && checkForDuplicateAddOn(itemId, currentType, index)) {
                              toast({
                                variant: "destructive",
                                title: "Duplicate Add-on",
                                description: `This ${currentType} is already added as an add-on. Please select a different one.`,
                              });
                              return;
                            }

                            onChange(itemId);

                            if (selectedOption) {
                              if (currentType === "product") {
                                setValue(`add_ons.${index}.access_control`, false);
                              } else {
                                const selectedFacility = facilityAsAddOns?.data?.find(f => f.id === itemId);
                                if (selectedFacility) {
                                  setValue(`add_ons.${index}.access_control`, selectedFacility.access_control);
                                }
                              }
                            }
                          }}
                          label="Item"
                          showLabel={false}
                        />
                      );
                    }}
                  />
                  {errors.add_ons?.[index]?.item_id && (
                    <span className="text-red-500 text-xs">
                      Required
                    </span>
                  )}
                </div>


                <div className="space-y-1">
                  <Label>
                    Type <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    name={`add_ons.${index}.type`}
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex rounded-md overflow-hidden">
                        <button
                          type="button"
                          onClick={() => onChange(FacilityAddOnType.Inclusive)}
                          className={cn(
                            "py-1 px-3 h-10 text-sm w-full",
                            value === FacilityAddOnType.Inclusive
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700"
                          )}
                        >
                          Inclusive
                        </button>
                        <button
                          type="button"
                          onClick={() => onChange(FacilityAddOnType.Exclusive)}
                          className={cn(
                            "py-1 px-3 h-10 text-sm w-full",
                            value === FacilityAddOnType.Exclusive
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700"
                          )}
                        >
                          Exclusive
                        </button>
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-1 col-span-1">
                  <Label>Description</Label>
                  <Controller
                    name={`add_ons.${index}.description`}
                    control={control}
                    rules={{
                      maxLength: {
                        value: 1000,
                        message: "Description should not exceed 1000 characters",
                      },
                    }}
                    render={({ field }) => (
                      <Textarea
                        rows={5}
                        placeholder="Enter description"
                        {...field}
                      />
                    )}
                  />
                  {errors.add_ons?.[index]?.description && (
                    <span className="text-red-500 text-xs">
                      {errors.add_ons?.[index]?.description?.message}
                    </span>
                  )}
                </div>

                <div className="row">
                  <div className="flex items-center justify-between">
                    <Label>Images</Label>
                    {(() => {
                      const selectedItemId = watch(`add_ons.${index}.item_id`);
                      const selectedItemType = watch(`add_ons.${index}.item_type`);
                      const images = selectedItemType === "facility"
                        ? (Array.isArray(facilityAsAddOns?.data?.find(f => f.id === selectedItemId)?.image_url)
                          ? (facilityAsAddOns?.data?.find(f => f.id === selectedItemId)?.image_url as string[])
                          : [])
                        : (productData?.data?.find(p => p.id === selectedItemId)?.image_url
                          ? [productData?.data?.find(p => p.id === selectedItemId)?.image_url as string]
                          : []);
                      return (
                        <span className="text-[11px] text-gray-500">{images.length} {images.length === 1 ? "image" : "images"}</span>
                      );
                    })()}
                  </div>
                  {(() => {
                    const selectedItemId = watch(`add_ons.${index}.item_id`);
                    const selectedItemType = watch(`add_ons.${index}.item_type`);
                    const images = selectedItemType === "facility"
                      ? (Array.isArray(facilityAsAddOns?.data?.find(f => f.id === selectedItemId)?.image_url)
                        ? (facilityAsAddOns?.data?.find(f => f.id === selectedItemId)?.image_url as string[])
                        : [])
                      : (productData?.data?.find(p => p.id === selectedItemId)?.image_url
                        ? [productData?.data?.find(p => p.id === selectedItemId)?.image_url as string]
                        : []);
                    const openLightbox = (startIndex: number) => {
                      setGallery({ open: true, images, index: startIndex });
                    };
                    return (
                      <div className="flex items-center gap-2 h-[96px] w-full border bg-background rounded-md bg-gray-100 overflow-x-auto px-2">
                        {images.length > 0 ? (
                          images.map((imgUrl, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => openLightbox(i)}
                              className="relative h-[72px] w-[72px] shrink-0 rounded overflow-hidden border hover:ring-2 hover:ring-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/80"
                              aria-label={`View image ${i + 1}`}
                            >
                              <img
                                src={`${VITE_VIEW_S3_URL}/${imgUrl}`}
                                alt={`Add-on ${i + 1}`}
                                className="h-full w-full object-cover"
                              />
                            </button>
                          ))
                        ) : (
                          <div className="flex items-center justify-center w-full">
                            <p className="text-xs text-gray-500">No images for selected facility</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {addOnFields.length >= 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeAddOn(index)}
                    className="mt-2"
                  >
                    <Trash2 className="text-red-500" />
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      appendAddOn({
                        item_id: undefined,
                        item_type: "facility",
                        type: FacilityAddOnType.Inclusive,
                        description: "",
                      })
                    }
                  >
                    <Plus className="text-primary" />
                  </Button>
                </div>
              )}
            </CardContent>
          ))}
        </Card>
      )}

      {gallery.open && (
        <div className="fixed inset-0 z-[60] top-0">
          <div
            className="absolute inset-0 -top-4 bg-black/70"
            onClick={() => setGallery({ open: false, images: [], index: 0 })}
          />
          <div className="absolute inset-0 flex items-center justify-center ">
            <div className="relative max-w-[90vw] min-h-[45vh] max-h-[85vh] w-full flex items-center justify-center ">
              <button
                type="button"
                className="absolute top-2 right-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                aria-label="Close gallery"
                onClick={() => setGallery({ open: false, images: [], index: 0 })}
              >
                <X className="h-5 w-5" />
              </button>

              {gallery.images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="absolute left-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                    aria-label="Previous image"
                    onClick={() =>
                      setGallery((g) => ({
                        ...g,
                        index: (g.index - 1 + g.images.length) % g.images.length,
                      }))
                    }
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                    aria-label="Next image"
                    onClick={() =>
                      setGallery((g) => ({
                        ...g,
                        index: (g.index + 1) % g.images.length,
                      }))
                    }
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              <img
                src={`${VITE_VIEW_S3_URL}/${gallery.images[gallery.index]}`}
                alt={`Gallery image ${gallery.index + 1}`}
                className="max-h-[85vh] max-w-[90vw] object-contain rounded shadow-lg"
              />
              {gallery.images.length > 0 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-white bg-black/50 rounded px-2 py-0.5">
                  {gallery.index + 1} / {gallery.images.length}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralInfoForm;

// Lightweight lightbox overlay for gallery (scoped within this file)
// Rendered conditionally when gallery.open is true
// Placed after export for co-location; relies on component-level state

